import { addWeeks, formatWeekRange, mondayOf } from '../../ProjectManagement/Sales/meetingsWeek';

/**
 * The one period control on Sales → Meetings, shared by Demo bookings,
 * Prospect meetings and SDR meetings (kept in `?period=`).
 *
 * - week: Monday 00:00 to next Monday 00:00, so this week's upcoming meetings show.
 * - 30 / 90: the last N days up to now.
 *
 * Each view applies it to its own date: Demo bookings to the booking date,
 * Prospect meetings to the meeting date, SDR meetings to the form submission.
 */
export type MeetingsPeriodId = 'week' | '30' | '90';

export const MEETINGS_PERIODS = [
  { id: 'week', label: 'This week' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
] as const satisfies ReadonlyArray<{ id: MeetingsPeriodId; label: string }>;

export const DEFAULT_MEETINGS_PERIOD: MeetingsPeriodId = '30';

export interface MeetingsPeriod {
  id: MeetingsPeriodId;
  from: string;
  to: string;
  /** "this week (5 Oct – 11 Oct)", "last 30 days". */
  label: string;
}

export function isMeetingsPeriodId(value: string | null): value is MeetingsPeriodId {
  return value === 'week' || value === '30' || value === '90';
}

export function meetingsPeriod(id: MeetingsPeriodId, now: Date = new Date()): MeetingsPeriod {
  if (id === 'week') {
    const monday = mondayOf(now);
    return {
      id,
      from: monday.toISOString(),
      to: addWeeks(monday, 1).toISOString(),
      label: `this week (${formatWeekRange(monday)})`,
    };
  }
  const days = Number(id);
  return {
    id,
    from: new Date(now.getTime() - days * 86_400_000).toISOString(),
    to: now.toISOString(),
    label: `last ${days} days`,
  };
}
